const API_URL = process.env.NEXT_PUBLIC_API_URL?.replace(/\/+$/, "") ?? "";
const TAB_SESSION_KEY = "riu.tab-session";

export function sessionRequestHeaders(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const token = window.sessionStorage.getItem(TAB_SESSION_KEY);
    return token ? { authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function rememberTabSession(response: Response) {
  const token = response.headers.get("x-riu-session");
  if (!token || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(TAB_SESSION_KEY, token);
  } catch {
    // 저장을 막아 둔 브라우저에서는 이 화면의 로그인만 유지하지 못한다.
  }
}

export function clearTabSession() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(TAB_SESSION_KEY);
  } catch {
    // 이미 비어 있으면 더 지울 값이 없다.
  }
}

const USER_ERROR_MESSAGES: Record<string, string> = {
  review_not_found: "리뷰를 찾을 수 없습니다. 화면을 새로고침한 뒤 다시 시도해 주세요.",
  reply_not_found: "등록된 답글을 찾을 수 없습니다. 화면을 새로고침해 주세요.",
  reply_already_exists: "이미 답글이 등록된 리뷰입니다.",
  platform_not_connected: "플랫폼 연결이 필요합니다.",
  sync_already_in_progress: "이미 리뷰를 동기화하고 있습니다.",
  brand_settings_forbidden: "매장을 선택한 뒤 저장해 주세요. 전체 매장 공통 저장은 지원하지 않습니다.",
  store_settings_only: "답글 설정은 매장 계정에서만 변경할 수 있습니다.",
  unauthorized: "로그인이 만료되었습니다. 이어서 작업할 수 없습니다. 다시 로그인해 주세요.",
  forbidden: "현재 권한으로 실행할 수 없습니다.",
  organization_scope_forbidden: "접근할 수 없는 브랜드입니다.",
  store_scope_forbidden: "접근할 수 없는 매장입니다.",
  store_scope_required: "작업할 매장을 선택해 주세요.",
  preview_limit_exceeded: "오늘 미리보기 5회를 모두 사용했습니다. 지금은 미리보기를 만들 수 없습니다. 내일 다시 시도해 주세요.",
  preview_reviews_not_found: "미리보기에 사용할 리뷰가 없습니다. 초안을 만들 수 없습니다. 리뷰를 먼저 수집해 주세요.",
  alimtalk_service_disabled: "알림톡 발송 기능이 꺼져 있습니다. 리우 운영 담당자에게 문의해 주세요.",
  invalid_phone_number: "휴대폰 번호 형식이 올바르지 않습니다. 010으로 시작하는 번호를 입력해 주세요.",
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

export function apiUrl(path: string) {
  if (!API_URL) {
    throw new Error(
      "화면 설정이 완료되지 않았습니다. 데이터를 불러올 수 없습니다. 관리자에게 문의해 주세요.",
    );
  }
  return `${API_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

function errorMessageFor(status: number) {
  if (status === 400) {
    return "요청 정보가 올바르지 않습니다. 변경 내용을 처리할 수 없습니다. 입력값을 확인해 다시 시도해 주세요.";
  }
  if (status === 403) {
    return "현재 권한으로 실행할 수 없습니다. 요청한 변경이 적용되지 않았습니다. 접근 가능한 범위에서 다시 시도해 주세요.";
  }
  return "서버 요청을 완료하지 못했습니다. 변경 내용이 반영되지 않았습니다. 잠시 후 다시 시도해 주세요.";
}

const CREDENTIAL_MESSAGE =
  "아이디 또는 비밀번호가 맞지 않습니다. 계정을 연결하지 못했습니다. 입력한 정보를 확인한 뒤 다시 시도해 주세요.";
const PROTECTION_MESSAGE =
  "계정 보호가 켜져 있습니다. 지금은 연동할 수 없습니다. 해당 서비스에서 보호를 해제한 뒤 다시 시도해 주세요.";
const CAPTCHA_MESSAGE =
  "자동 확인을 통과하지 못했습니다. 계정을 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.";
const DELAY_MESSAGE =
  "플랫폼 화면이 열리지 않았습니다. 계정 확인이 끝나지 않았습니다. 잠시 후 다시 시도해 주세요.";

function containsScreenJargon(message: string) {
  return /[A-Za-z]{3,}|[\[\]{}=_]|세션|캐시|자동화|명령 서버|타임아웃|캡차/.test(message);
}

export function toScreenErrorMessage(message: string, status = 500) {
  const known = USER_ERROR_MESSAGES[message];
  if (known) return known;
  if (!containsScreenJargon(message) && /[가-힣]/u.test(message)) return message;
  if (/timeout|Navigation|Failed to fetch|ms exceeded|타임아웃/i.test(message)) return DELAY_MESSAGE;
  if (/아이디 또는 비밀번호|로그인 실패/.test(message)) return CREDENTIAL_MESSAGE;
  if (/보호\s*조치|아이디 보호|계정 보호/.test(message)) return PROTECTION_MESSAGE;
  if (/캡차|captcha/i.test(message)) return CAPTCHA_MESSAGE;
  return errorMessageFor(status);
}

function getUserErrorMessage(
  status: number,
  errorBody: { message?: unknown; error?: unknown; code?: unknown } | null,
) {
  const code = typeof errorBody?.code === "string" ? errorBody.code : undefined;
  const serverMessage = [errorBody?.message, errorBody?.error]
    .find((value): value is string => typeof value === "string" && value.trim().length > 0)
    ?.trim();
  const localized = code ? USER_ERROR_MESSAGES[code] : undefined;
  if (localized) return localized;
  if (serverMessage) return toScreenErrorMessage(serverMessage, status);
  return errorMessageFor(status);
}

type RequestOptions = RequestInit & {
  notifyUnauthorized?: boolean;
};

const STALE_SCOPE_CODES = ["organization_scope_forbidden", "store_scope_forbidden"];

function responseCode(errorBody: { code?: unknown; error?: unknown } | null): string | undefined {
  if (typeof errorBody?.code === "string") return errorBody.code;
  if (typeof errorBody?.error === "string") return errorBody.error;
  return undefined;
}

function isStaleScope(code: string | undefined): boolean {
  return typeof code === "string" && STALE_SCOPE_CODES.includes(code);
}

export async function requestJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { notifyUnauthorized = true, ...init } = options;
  const response = await fetch(apiUrl(path), {
    ...init,
    cache: init.cache ?? "no-store",
    credentials: "include",
    headers: {
      accept: "application/json",
      ...sessionRequestHeaders(),
      ...init.headers,
    },
  });
  rememberTabSession(response);
  if (response.status === 204) return null as T;
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const errorBody =
      data && typeof data === "object"
        ? data as { message?: unknown; error?: unknown; code?: unknown; details?: unknown }
        : null;
    const code = responseCode(errorBody);
    const isApplicationSessionExpired = errorBody === null || code === "unauthorized";
    if (
      response.status === 401
      && notifyUnauthorized
      && isApplicationSessionExpired
      && typeof window !== "undefined"
    ) {
      window.dispatchEvent(new Event("riu:unauthorized"));
    }
    if (response.status === 403 && isStaleScope(code) && typeof window !== "undefined") {
      window.dispatchEvent(new Event("riu:scope-stale"));
    }
    throw new ApiError(
      getUserErrorMessage(response.status, errorBody),
      response.status,
      code,
      errorBody?.details,
    );
  }
  return data as T;
}

export async function downloadFile(path: string, fallbackName: string) {
  const response = await fetch(apiUrl(path), {
    credentials: "include",
    headers: sessionRequestHeaders(),
  });
  if (response.status === 401) {
    window.dispatchEvent(new Event("riu:unauthorized"));
    throw new ApiError("로그인이 만료되었습니다. 파일을 받을 수 없습니다. 다시 로그인한 뒤 시도해 주세요.", 401);
  }
  if (!response.ok) {
    throw new ApiError("파일 생성에 실패했습니다.", response.status);
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const fileName = disposition.match(/filename="?([^"]+)"?/)?.[1] ?? fallbackName;
  const objectUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(objectUrl);
}
